import React from 'react';
import { BaseButton, useT } from '@repo/common';

const Chevron = ({ direction = 'left' }) => {
  const isLeft = direction === 'left';
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={isLeft ? '' : 'rotate-180'}
    >
      <path
        d='M10 4L6 8L10 12'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const Pagination = ({ current = 1, totalPages = 10, onPageChange }) => {
  const t = useT();
  // 최대 10개 페이지 번호 표시
  // current가 5 이상이면 current를 6번째 위치에 배치
  const getVisiblePages = () => {
    if (current < 5) {
      // current가 5 미만이면 1부터 10까지 표시
      return Array.from(
        { length: Math.min(10, totalPages) },
        (_, idx) => idx + 1,
      );
    } else {
      // current가 5 이상이면 current를 6번째 위치에 배치
      const start = current - 5;
      const end = Math.min(start + 10, totalPages);
      return Array.from({ length: end - start }, (_, idx) => start + idx + 1);
    }
  };

  const visiblePages = getVisiblePages();
  const isFirstPage = current === 1;
  const isLastPage = current === totalPages;

  const handlePrevious = () => {
    if (!isFirstPage && onPageChange) {
      onPageChange(current - 1);
    }
  };

  const handleNext = () => {
    if (!isLastPage && onPageChange) {
      onPageChange(current + 1);
    }
  };

  const handlePageClick = (page) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  return (
    <div className='flex items-center justify-center gap-[16px]'>
      <BaseButton
        type='text'
        size='s'
        aria-label={t('previous page', '이전 페이지')}
        onClick={handlePrevious}
        isDisabled={isFirstPage}
        icon={<Chevron direction='left' />}
        className={`h-auto min-w-0 border-0 bg-transparent p-2 rounded-lg ${
          isFirstPage
            ? 'text-grayscale-gray03 cursor-not-allowed'
            : 'text-grayscale-gray06 hover:bg-grayscale-gray02 hover:text-grayscale-gray06'
        }`}
      />

      <div className='flex items-center justify-center gap-[6px]'>
        {visiblePages.map((page, index) => {
          const isActive = page === current;
          const isNext = page === current + 1;
          return (
            <BaseButton
              key={page}
              type='text'
              size='s'
              hideIcon
              typo={false}
              isStatic
              onClick={() => handlePageClick(page)}
              className={`min-w-0 cursor-pointer border-0 p-1.5 rounded-lg size-[32px] text-13 font-medium leading-[1.4] transition-colors ${
                index >= 5 ? 'hidden tabletH:flex' : 'flex'
              } ${
                isActive
                  ? 'bg-green-deep02 text-text-inverse'
                  : isNext
                    ? 'bg-grayscale-gray02 text-text-secondary'
                    : 'bg-transparent text-text-secondary hover:bg-grayscale-gray02'
              }`}
              textWrapperClass='h-auto'
              aria-current={isActive ? 'page' : undefined}
            >
              {page}
            </BaseButton>
          );
        })}
      </div>

      <BaseButton
        type='text'
        size='s'
        aria-label={t('next page', '다음 페이지')}
        onClick={handleNext}
        isDisabled={isLastPage}
        icon={<Chevron direction='right' />}
        className={`h-auto min-w-0 border-0 bg-transparent p-2 rounded-lg ${
          isLastPage
            ? 'text-grayscale-gray03 cursor-not-allowed'
            : 'text-grayscale-gray06 hover:bg-grayscale-gray02 hover:text-grayscale-gray06'
        }`}
      />
    </div>
  );
};

export default Pagination;
